import { JsonValue } from '@backstage/types';

/**
 * ApplicationResponse - Response type for application creation
 */
export type ApplicationResponse = {
  success: boolean;
  applicationId: number;
  applicationName: string;
  applicationDescription: string;
  ownerLogin: string;
  message?: string;
  resources?: {
    application?: {
      createExperimentalApplication?: {
        success: boolean;
        applicationId: number;
        message?: string;
      };
      deleteExperimentalApplication?: {
        success: boolean;
        applicationId: number;
        message?: string;
      };
    };
  };
};

/**
 * ApplicationDeleteResponse - Response type for application deletion
 */
export type ApplicationDeleteResponse = {
  success: boolean;
  applicationId: number;
  message?: string;
  resources?: {
    application?: {
      deleteExperimentalApplication?: {
        success: boolean;
        applicationId: number;
        message?: string;
      };
    };
  };
};

/**
 * GithubFile - Response type for GitHub file
 */
export type GithubFile = {
  type: 'dir' | 'file' | 'submodule' | 'symlink';
  size: number;
  name: string;
  path: string;
  content?: string | undefined;
  sha: string;
  url: string;
  git_url: string | null;
  html_url: string | null;
  download_url: string | null;
};

/**
 * GitHubTeam - Response type for GitHub team
 */
export type GitHubTeam = {
  id: number;
  node_id: string;
  url: string;
  html_url: string;
  name: string;
  slug: string;
  description: string | null;
  privacy?: string;
  permission: string;
  members_url: string;
  repositories_url: string;
  parent?: null | {
    id: number;
    node_id: string;
    url: string;
    name: string;
    slug: string;
    description: string | null;
    privacy?: string;
    permission: string;
  };
};

/**
 * GithubRepo - Response type for GitHub repository
 */
export type GithubRepo = {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  default_branch?: string;
  archived?: boolean;
};

/**
 * cnapResponse - Response type for cnap
 */
export interface cnapResponse {
  tenantName: string;
  status?: string;
  message?: string;
  code?: number;
  description?: string;
}

/**
 * AdGroups
 */
export interface AdGroups {
  adGroups?: {
    groupName: string;
    groupId: string;
  }[];
}

/**
 * ApplicationResponse - Response type for application creation
 */
export type AdGroupResponse = {
  success: boolean;
  adGroups?: {
    groupName: string;
    groupId: string;
  }[];
  message?: string;
  resources?: {
    application?: {
      createApplicationAdGroup?: {
        success: boolean;
        applicationId: number;
        message?: string;
        application?: {
          resources?: {
            adGroupsPaged?: {
              adGroups?: {
                groupName: string;
                groupId: string;
              }[];
            };
          };
        };
      };
    };
  };
};

/**
 * Cnap Create Tenancy Connection Response - Response type for cnap tenancy creation
 */
export type CnapCreateTenancyResponse = {
  success: boolean;
  message?: string;
  resources?: {
    application?: {
      createCnapTenancy?: {
        success: boolean;
        applicationId: number;
        message?: string;
      };
    };
  };
};

/**
 * Cnap Delete Tenancy Connection Response - Response type for cnap tenancy creation
 */
export type CnapDeleteTenancyResponse = {
  success: boolean;
  message?: string;
  resources?: {
    application?: {
      deleteCnapTenancy?: {
        success: boolean;
        applicationId: number;
        message?: string;
      };
    };
  };
};

/**
 * ApplicationResponse - Response type for application creation
 */
export type AdGroupDeleteResponse = {
  success: boolean;
  message?: string;
  resources?: {
    application?: {
      deleteApplicationAdGroup?: {
        success: boolean;
        message?: string;
      };
    };
  };
};

/**
 * AdGroup - Response type for AD group
 */
export type AdGroup = {
  groupName: string;
  groupId: string;
};

/**
 * Application - Response type for application
 */
export type Application = {
  name: string;
  applicationId: number;
  owner: {
    firstName: string;
    lastName: string;
  };
  createdAt: Date;
  resources: {
    adGroupsPaged: {
      adGroups: AdGroup[];
    };
  };
};

/**
 * ExperimentalApps - Response type for experimental apps
 */
export type ExperimentalApps = {
  applicationId: number;
  applicationName: string;
  adGroups: {
    adGroupId: string;
    adGroupName: string;
  }[];
};

/**
 * ApplicationsPaged - Response type for applications paged
 */
export type ApplicationsPaged = {
  totalCount: number;
  applications?: Application[];
};

/**
 * UserResources - Response type for user resources
 */
export type UserResources = {
  applicationsPaged: ApplicationsPaged;
};

/**
 * User - Response type for user
 */
export type User = {
  resources: UserResources;
};

/**
 * Identity - Response type for identity
 */
export type Identity = {
  user: User;
};

/**
 * RepoCustomProperty - Response type for repository custom property
 */
export type RepoCustomProperty = {
  name: string;
  html_url: string;
  property_name: string;
  property_value: string;
};

/**
 * Idea - Response type for idea
 */
export type Idea = {
  applicationName: string;
  applicationId?: number;
  userLogin?: string;
  description: string;
  resources?: {
    adGroups?: Array<{ groupName: string; groupId: string }>;
    tenantName?: string;
    githubRepo?: string;
    githubRepoUrl?: string;
  };
  // ...other properties
};

/**
 * Task Type Enum
 */
export enum TaskType {
  APPLICATION_CREATION = 'Application Creation',
  GITHUB_REPO_CREATION = 'GitHub Repository Creation',
  GITHUB_REPO_POST_CREATION = 'GitHub Repository Post Creation',
  GITHUB_REPO_POST_DELETE = 'GitHub Repository Post Deletion',
  AD_GROUPS_CREATION = 'AD Groups Creation',
  CNAP_TENANCY_CREATION = 'CNAP Tenancy Creation',
  CNAP_TENANCY_CONNECTION_CREATION = 'CNAP Tenancy Conection Creation',
  APPLICATION_DELETE = 'Application Deletion',
  GITHUB_REPO_DELETE = 'GitHub Repository Deletion',
  AD_GROUPS_DELETE = 'AD Groups Deletion',
  CNAP_TENANCY_DELETE = 'CNAP Tenancy Deletion',
  CNAP_TENANCY_CONNECTION_DELETE = 'CNAP Tenancy Conection Deletion',
}

/**
 * Publish event signals
 */
export enum EventType {
  CNAP_TENANCY = 'cnap-tenancy',
  AD_GROUPS = 'ad-groups',
  AD_GROUPS_DELETION = 'ad-groups-deletion',
  GITHUB_REPO = 'github-repo',
  GITHUB_REPO_DELETION = 'github-repo-deletion',
  APPLICATION = 'app-creation',
  APPLICATION_DELETION = 'app-deletion',
  CNAP_TENANCY_DELETION = 'cnap-tenancy-deletion',
  CNAP_TENANCY_PRECHECK = 'cnap-tenancy-precheck',
  GITHUB_REPO_PRECHECK = 'github-repo-precheck',
  AD_GROUPS_PRECHECK = 'ad-groups-precheck',
  APPLICATION_PRECHECK = 'app-precheck',
}

/**
 * Wrapped http response type
 */
export type HttpResponse<T = JsonValue> = {
  status: number;
  data?: T;
  error?: string;
};

/**
 * Application Context - Response type for application context
 */
export type ApplicationContext = {
  name: string;
  description: string;
  userLogin: string;
  applicationId?: number;
  userId?: string;
  status?: string;
  adGroups?: Array<{ groupName: string; groupId: string }>;
  tenantName?: string;
  githubRepo?: string;
  githubRepoUrl?: string;
  tasks?: [TaskType];
  changed?: boolean;
  [key: string]: any;
  // ...other properties
};
